/* eslint-disable no-control-regex */

import { Button, Group, Stack } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';
import { z } from 'zod';

import { UploadFile } from '@components';

interface UploadFileModalProps {
  onHide: () => void;
  onFileSubmit: (file: File) => void;
}

// Regex for validating filenames with only ASCII characters
const asciiRegex = /^[\x00-\x7F]*$/;

const uploadSchema = z.object({
  file: z.instanceof(File, { message: 'File is required' }).refine((file) => asciiRegex.test(file.name), {
    message: 'File name contains invalid characters.',
  }),
});

type UploadFormValues = z.infer<typeof uploadSchema>;

export const UploadFileModal: React.FC<UploadFileModalProps> = ({ onHide, onFileSubmit }) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<UploadFormValues>({
    initialValues: {
      file: null,
    },
    validate: zodResolver(uploadSchema),
    validateInputOnBlur: true,
  });

  const onSubmit = async (values: UploadFormValues) => {
    setSubmitting(true);

    try {
      onFileSubmit(values.file);
      form.reset();
    } finally {
      setSubmitting(false);
      onHide();
    }
  };

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack gap="sm" mb="xl">
        <UploadFile {...form.getInputProps('file')} isMulti={false} type="xlsx" p="xs" />
      </Stack>

      <Group justify="center">
        <Button type="button" onClick={onHide} disabled={submitting} size="lg" variant="transparent">
          Cancel
        </Button>

        <Button type="submit" loading={submitting} disabled={!form.isValid() || !form?.values?.file} variant="filled" size="lg">
          Upload
        </Button>
      </Group>
    </form>
  );
};
